import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@strapi/design-system';
import { auth } from '@strapi/helper-plugin';
import Editor from '../CKEditor';
import MediaLib from '../MediaLib';
import { config, flahscardsConfig } from '../../config/ckeditor';
import embedConstructor from '../../utils/embedConstructor';
import axe from 'axe-core';
import { AccessibilityChecker } from '../AccessibilityChecker/AccessibilityChecker';
import { extractNodesFromViolations } from '#shared/utils/extractNodesFromViolations';
import { sortResultsByDOMOrder } from '../../utils/sortDOMElements';
import { cleanHighlightedNodes, highlightDomElements } from '../../utils/highlightDomElements';
import { GlossaryTermsModal } from '../GlossaryTermsModal';
import '../../utils/highlightDomElements/highlightStyles.css';
import { hidePanel, showPanel, updateResults } from '#shared/redux/reducers/accessibilityChecker';
const Wysiwyg = props => {
  const checkerReducer = useSelector(state => state.accessibilityChecker);
  const activityDraft = useSelector(state => state.activityDraft);
  const isPanelVisible = checkerReducer?.isPanelVisible;
  const dispatch = useDispatch();
  const {
    formatMessage
  } = useIntl();
  const {
    description,
    error,
    intlLabel,
    placeholder,
    name,
    onChange,
    value,
    disabled,
    required = false,
    allowAccessibility = false,
    allowGlossaryTerms,
    isFlashcardEditor = false
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [editor, setEditor] = useState(null);
  const [accessibilityData, setAccessibilityData] = useState(null);
  const [isAccessibilityVisible, setIsAccessibilityVisible] = useState(false);
  const [glossaryTermsData, setGlossaryTermsData] = useState(null);
  const [isGlossaryTermsModalOpen, setIsGlossaryTermsModalOpen] = useState(false);
  const DELAY_TIME_TO_CHECK_ACCESSIBILITY = 2000;
  const closeAccessibilityChecker = checkerReducer ? () => dispatch(hidePanel()) : () => setIsAccessibilityVisible(false);
  const openAccessibilityChecker = checkerReducer ? () => dispatch(showPanel()) : () => setIsAccessibilityVisible(true);
  const glossaryTermAction = editor => {
    if (!editor) return;
    setGlossaryTermsData({
      ...editor,
      selectData: glossaryTermsData
    });
    setIsGlossaryTermsModalOpen(true);
  };
  const handleChange = async data => {
    if (data) {
      await Promise.all(data.map(async file => {
        const content = await embedConstructor(file);
        const viewFragment = editor.data.processor.toView(content);
        const modelFragment = editor.data.toModel(viewFragment);
        editor.model.insertContent(modelFragment, editor.model.document.selection);
      }));
    }
  };
  const toggleMediaLib = editor => {
    if (editor) {
      setEditor(editor);
    }
    setIsOpen(prev => !prev);
  };
  const errorMessage = error ? formatMessage({
    id: error,
    defaultMessage: error
  }) : '';
  const label = intlLabel?.id ? formatMessage({
    id: intlLabel?.id,
    defaultMessage: intlLabel.defaultMessage
  }, {
    ...intlLabel?.values
  }) : name;
  const hint = description ? formatMessage({
    id: description?.id,
    defaultMessage: description.defaultMessage
  }, {
    ...description?.values
  }) : '';
  const debounceCallback = sortedResults => checkerReducer && dispatch(updateResults(sortedResults));
  const buttonCallback = () => openAccessibilityChecker();
  const runAuditory = async (editor, axeCallback) => {
    if (!allowAccessibility) return;
    cleanHighlightedNodes();
    const domRoots = editor.editing.view.domRoots;
    const [key, value] = domRoots.entries().next().value;
    await axe.run(value, (error, results) => {
      if (error) throw error;
      const violations = extractNodesFromViolations(results.violations);
      const sortedResults = sortResultsByDOMOrder(violations);
      setAccessibilityData(sortedResults);
      highlightDomElements(sortedResults);
      axeCallback(sortedResults);
    });
  };
  const checkAccessibility = async editor => {
    await runAuditory(editor, buttonCallback);
  };
  config.allowAccessibility = allowAccessibility;
  config.checkAccessibility = {
    checkAccessibility
  };
  config.strapiMediaLib = {
    onToggle: toggleMediaLib,
    label: label
  };
  config.allowGlossary = allowGlossaryTerms;
  config.glossaryTermAction = {
    glossaryTermAction
  };
  config.strapiUpload = {
    uploadUrl: `${strapi.backendURL}/upload`,
    headers: {
      Authorization: 'Bearer ' + auth.getToken()
    }
  };
  const handleContentChange = (editor, data) => {
    setEditor(editor);
    onChange(data);
  };
  const currentValue = value && value?.startsWith('<script') ? '<p>&nbsp;</p>' + value : value;
  useEffect(() => {
    const delayAccessibilityWhileTyping = setTimeout(async () => editor && (await runAuditory(editor, debounceCallback)), DELAY_TIME_TO_CHECK_ACCESSIBILITY);
    return () => clearTimeout(delayAccessibilityWhileTyping);
  }, [value, editor]);
  return /*#__PURE__*/React.createElement(Stack, {
    spacing: 2
  }, /*#__PURE__*/React.createElement(Stack, {
    horizontal: true,
    spacing: 1
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "pi",
    fontWeight: "bold",
    textColor: "neutral800"
  }, label), required && /*#__PURE__*/React.createElement(Typography, {
    variant: "pi",
    fontWeight: "bold",
    textColor: "danger600",
    "aria-hidden": true
  }, "*")), /*#__PURE__*/React.createElement(AccessibilityChecker, {
    data: accessibilityData,
    isVisible: checkerReducer ? isPanelVisible : isAccessibilityVisible,
    onClose: closeAccessibilityChecker
  }), /*#__PURE__*/React.createElement(GlossaryTermsModal, {
    glossaryTermsData: glossaryTermsData,
    isGlossaryTermsModalOpen: isGlossaryTermsModalOpen,
    setIsGlossaryTermsModalOpen: setIsGlossaryTermsModalOpen,
    businessLine: activityDraft?.businessLineId
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      lineHeight: 'normal'
    }
  }, /*#__PURE__*/React.createElement(Editor, {
    name: name,
    onChange: handleContentChange,
    value: currentValue || '',
    config: isFlashcardEditor ? flahscardsConfig : config,
    disabled: disabled,
    onLoad: setEditor
  })), (!hint || error) && /*#__PURE__*/React.createElement(Typography, {
    as: "p",
    variant: "pi",
    id: `${name}-hint`,
    textColor: "neutral600"
  }, hint), errorMessage && /*#__PURE__*/React.createElement(Box, {
    paddingTop: 1
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "pi",
    textColor: "danger600",
    "data-strapi-field-error": true
  }, errorMessage)), /*#__PURE__*/React.createElement(MediaLib, {
    onToggle: toggleMediaLib,
    isOpen: isOpen,
    onChange: handleChange
  }));
};
Wysiwyg.defaultProps = {
  errors: [],
  inputDescription: null,
  label: '',
  noErrorsDescription: false,
  value: ''
};
Wysiwyg.propTypes = {
  errors: PropTypes.array,
  inputDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.shape({
    id: PropTypes.string,
    params: PropTypes.object
  })]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.shape({
    id: PropTypes.string,
    params: PropTypes.object
  })]),
  name: PropTypes.string.isRequired,
  noErrorsDescription: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string
};
export default Wysiwyg;